import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, message } from 'antd';
import { TrainingProjectSessionModel } from '../../models';
import ReactJson from 'react-json-view';

interface Props {
    session?: TrainingProjectSessionModel;
    visible: boolean;
    onClose: () => void;
}

const TrainingProjectSessionParametersModal: React.FC<Props> = ({ session, visible, onClose }) => {
    const parameters = React.useMemo(() => session?.parameters ?? {}, [session]);
    const jsonString = React.useMemo(() => {
        try {
            return JSON.stringify(parameters, null, 2);
        } catch (error) {
            console.error('Unable to stringify training session parameters', error);
            return '{}';
        }
    }, [parameters]);

    const handleCopyJson = React.useCallback(async (): Promise<void> => {
        try {
            if (!navigator.clipboard || !navigator.clipboard.writeText) {
                throw new Error('Clipboard API is not available');
            }

            await navigator.clipboard.writeText(jsonString);
            message.success('Parameters copied to clipboard');
        } catch (error) {
            message.error('Unable to copy parameters');
        }
    }, [jsonString]);

    const handleDownloadJson = React.useCallback((): void => {
        if (!session) {
            return;
        }

        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `training_session_parameters_${session.id}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success('Download started');
    }, [session, jsonString]);

    return (
        <Modal
            className="alpha-portal-dialog training-project-session-parameters-modal"
            bodyStyle={{ maxHeight: 'calc(100vh - 310px)' }}
            width={800}
            visible={visible}
            title="Parameters"
            onCancel={onClose}
            maskClosable={false}
            centered
            footer={[
                <Button key="copy-json" onClick={handleCopyJson}>
                    Copy JSON
                </Button>,
                <Button key="download-json" onClick={handleDownloadJson}>
                    Download .json
                </Button>,
                <Button key="back" type="primary" onClick={onClose}>
                    Close
                </Button>
            ]}
        >
            <ReactJson src={parameters} displayDataTypes={false} name={false} enableClipboard={false} />
        </Modal>
    );
};

export default observer(TrainingProjectSessionParametersModal);
